import { useCallback, useMemo, useState } from "react"
import type React from "react"

import type { RunResultsMetricRenderAdaptersById } from "../types"

export interface RunResultsTableColumn {
  key: string
  label: string
  width?: string
  minWidth?: string
  maxWidth?: string
  fullLabel?: string
}

const METRIC_COLUMN_MIN_WIDTH_PX = 100
const DEFAULT_COLUMN_MIN_WIDTH_PX = 96
const COLUMN_MIN_WIDTHS_PX: Record<string, number> = {
  actions: 70,
  compareBy: 120,
  errorStatus: 44,
  expectedBehaviors: 160,
  expectedOutputJson: 160,
  index: 44,
  inputStr: 120,
  mutationName: 120,
  simulationOutputId: 56,
  transcript: 160,
}

export function parsePixelWidth(value: string | undefined): number | null {
  if (!value) {
    return null
  }

  const match = value.trim().match(/^(\d+(?:\.\d+)?)px$/)
  if (!match) {
    return null
  }

  return Number(match[1])
}

export function getRunResultsColumnMinWidthPx(
  column: RunResultsTableColumn,
  metricRenderAdaptersById: RunResultsMetricRenderAdaptersById,
): number {
  if (column.key in metricRenderAdaptersById) {
    return METRIC_COLUMN_MIN_WIDTH_PX
  }

  return (
    COLUMN_MIN_WIDTHS_PX[column.key] ??
    parsePixelWidth(column.minWidth) ??
    parsePixelWidth(column.width) ??
    DEFAULT_COLUMN_MIN_WIDTH_PX
  )
}

export function getRunResultsColumnMinWidthStyle(
  column: RunResultsTableColumn,
  metricRenderAdaptersById: RunResultsMetricRenderAdaptersById,
): string {
  if (column.key in metricRenderAdaptersById) {
    return `${METRIC_COLUMN_MIN_WIDTH_PX}px`
  }

  return (
    column.minWidth ??
    column.width ??
    `${getRunResultsColumnMinWidthPx(column, metricRenderAdaptersById)}px`
  )
}

interface UseRunResultsTableColumnsParams {
  allColumns: RunResultsTableColumn[]
  metricRenderAdaptersById: RunResultsMetricRenderAdaptersById
}

interface RunResultsTableColumnsController {
  visibleColumns: Set<string>
  columnOrder: string[]
  draggedColumn: string | null
  effectiveVisibleColumns: Set<string>
  effectiveColumnOrder: string[]
  visibleOrderedColumns: RunResultsTableColumn[]
  tableMinWidthPx: number
  setVisibleColumns: React.Dispatch<React.SetStateAction<Set<string>>>
  setColumnOrder: React.Dispatch<React.SetStateAction<string[]>>
  toggleColumn: (key: string) => void
  handleDragStart: (e: React.DragEvent, columnKey: string) => void
  handleDragOver: (e: React.DragEvent) => void
  handleDrop: (e: React.DragEvent, targetColumnKey: string) => void
  handleDragEnd: () => void
}

/**
 * Owns column visibility, ordering, and drag-and-drop reordering for the run
 * results table. Derives the effective visible set / order (falling back to all
 * columns until the user customizes them) and the table's minimum width.
 */
export function useRunResultsTableColumns({
  allColumns,
  metricRenderAdaptersById,
}: UseRunResultsTableColumnsParams): RunResultsTableColumnsController {
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set())
  const [columnOrder, setColumnOrder] = useState<string[]>([])
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null)

  const effectiveVisibleColumns = useMemo(() => {
    if (visibleColumns.size > 0) {
      return visibleColumns
    }
    if (allColumns.length > 0) {
      return new Set(
        allColumns.filter((col) => col.key !== "agentId").map((col) => col.key),
      )
    }
    return new Set<string>()
  }, [visibleColumns, allColumns])

  const effectiveColumnOrder = useMemo(() => {
    if (columnOrder.length > 0) {
      return columnOrder
    }
    if (allColumns.length > 0) {
      return allColumns
        .filter((col) => col.key !== "agentId")
        .map((col) => col.key)
    }
    return []
  }, [columnOrder, allColumns])

  const visibleOrderedColumns = useMemo(
    () =>
      effectiveColumnOrder
        .filter((columnKey) => effectiveVisibleColumns.has(columnKey))
        .map((columnKey) => allColumns.find((col) => col.key === columnKey))
        .filter((col): col is RunResultsTableColumn => col != null),
    [allColumns, effectiveColumnOrder, effectiveVisibleColumns],
  )

  const tableMinWidthPx = useMemo(
    () =>
      visibleOrderedColumns.reduce(
        (total, column) =>
          total +
          getRunResultsColumnMinWidthPx(column, metricRenderAdaptersById),
        0,
      ),
    [metricRenderAdaptersById, visibleOrderedColumns],
  )

  const toggleColumn = useCallback((key: string) => {
    setVisibleColumns((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(key)) {
        newSet.delete(key)
      } else {
        newSet.add(key)
      }
      return newSet
    })
  }, [])

  const handleDragStart = useCallback(
    (e: React.DragEvent, columnKey: string) => {
      setDraggedColumn(columnKey)
      e.dataTransfer.effectAllowed = "move"
    },
    [],
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent, targetColumnKey: string) => {
      e.preventDefault()

      if (!draggedColumn || draggedColumn === targetColumnKey) {
        setDraggedColumn(null)
        return
      }

      setColumnOrder((prevOrder) => {
        const newOrder = [...prevOrder]
        const draggedIndex = newOrder.indexOf(draggedColumn)
        const targetIndex = newOrder.indexOf(targetColumnKey)

        newOrder.splice(draggedIndex, 1)

        const adjustedTargetIndex =
          draggedIndex < targetIndex ? targetIndex - 1 : targetIndex
        newOrder.splice(adjustedTargetIndex, 0, draggedColumn)

        return newOrder
      })

      setDraggedColumn(null)
    },
    [draggedColumn],
  )

  const handleDragEnd = useCallback(() => {
    setDraggedColumn(null)
  }, [])

  return {
    visibleColumns,
    columnOrder,
    draggedColumn,
    effectiveVisibleColumns,
    effectiveColumnOrder,
    visibleOrderedColumns,
    tableMinWidthPx,
    setVisibleColumns,
    setColumnOrder,
    toggleColumn,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  }
}
